import type React from "react";
import { useState } from "react";

export const HomePage: React.FC = () => {
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const testAPI = async () => {
    setLoading(true);
    try {
      const response = await fetch("http://localhost:8080/api/test");
      const data = await response.json();
      setMessage(JSON.stringify(data, null, 2));
    } catch (error) {
      setMessage(`Error: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  const testAI = async () => {
    setLoading(true);
    try {
      const response = await fetch("http://localhost:8080/api/ai-test", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          prompt: "Hello! Please respond with: Frontend is working with AI!",
        }),
      });
      const data = await response.json();
      setMessage(JSON.stringify(data, null, 2));
    } catch (error) {
      setMessage(`Error: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">Odoo Digital Ocean Setup</h1>
              <span className="ml-3 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                Community Edition 18
              </span>
            </div>
            <div className="text-sm text-gray-500">AI-Powered Module Recommendations</div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">System Status Check</h2>

          <div className="space-y-4">
            <div className="flex gap-4">
              <button
                type="button"
                onClick={testAPI}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {loading ? "Testing..." : "Test API Connection"}
              </button>

              <button
                type="button"
                onClick={testAI}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {loading ? "Testing..." : "Test AI Integration"}
              </button>
            </div>

            {message && (
              <div className="mt-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Response:</h3>
                <pre className="bg-gray-100 p-4 rounded-md text-sm overflow-auto">{message}</pre>
              </div>
            )}
          </div>

          <div className="mt-8 p-4 bg-blue-50 rounded-lg">
            <h3 className="text-lg font-medium text-blue-900 mb-2">
              🚀 Welcome to Odoo DigitalOcean Setup Tool
            </h3>
            <p className="text-blue-700 mb-4">
              This tool helps you deploy Odoo Community Edition 18 on DigitalOcean with
              deterministic module recommendations.
            </p>
            <div className="space-y-2 text-sm text-blue-600">
              <div>✅ Frontend: React + TypeScript + Tailwind CSS</div>
              <div>✅ Backend: Node.js + Express + TypeScript</div>
              <div>✅ Database: PostgreSQL</div>
              <div>✅ Recommendations: Deterministic rule-based engine</div>
              <div>✅ Cloud: DigitalOcean API integration</div>
            </div>

            <div className="mt-4 pt-4 border-t border-blue-200">
              <a
                href="/requirements"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                📋 Get Module Recommendations
              </a>
            </div>
          </div>

          <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
            <h4 className="text-md font-medium text-yellow-900 mb-2">🔧 Development Mode</h4>
            <p className="text-yellow-700 text-sm">
              The application is running in development mode with simplified authentication. Use the
              buttons above to test the API and AI integration.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-sm text-gray-500">
            <p>Powered by AI • Built for Digital Ocean • Odoo Community Edition 18</p>
            <p className="mt-2">
              This tool helps you deploy Odoo with the right modules and server configuration.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
